import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MailX, 
  CheckCircle2, 
  ArrowLeft, 
  AlertTriangle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function UnsubscribePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleUnsubscribe = async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/user/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to unsubscribe');
      }

      setIsUnsubscribed(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Success State
  if (isUnsubscribed) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="unsubscribe-success-page">
        <Header />
        
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <Card className="max-w-md w-full p-8 text-center bg-white border border-gray-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Successfully Unsubscribed
            </h1>
            
            <p className="text-gray-600 mb-6">
              You have been unsubscribed from all marketing communications. 
              You will still receive essential account-related messages.
            </p>

            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#00D95F] hover:bg-[#00C054]"
              >
                Back to Dashboard
              </Button>
              
              <Link 
                to="/data-deletion" 
                className="block text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                Want to delete your account? Request Data Deletion
              </Link>
            </div>
          </Card>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="unsubscribe-page">
      <Header />
      
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full p-8 bg-white border border-gray-200">
          <Button
            variant="ghost"
            className="mb-4 p-0 h-auto text-gray-600 hover:text-gray-900"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MailX className="w-8 h-8 text-orange-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Unsubscribe from Communications
            </h1>
            
            <p className="text-gray-600">
              Are you sure you want to unsubscribe from GiriChat marketing communications?
            </p>
          </div>

          {user && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {user.email}
              </p>
              {user.phone && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Phone:</span> {user.phone}
                </p>
              )}
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">What happens when you unsubscribe:</p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>No more promotional emails or WhatsApp messages</li>
                  <li>You'll still receive important account notifications</li>
                  <li>Your account will remain active</li>
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <Button 
              onClick={handleUnsubscribe}
              disabled={isLoading}
              variant="outline"
              className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
              data-testid="confirm-unsubscribe-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <MailX className="w-4 h-4 mr-2" />
              )}
              Yes, Unsubscribe Me
            </Button>

            <Button 
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#00D95F] hover:bg-[#00C054]"
            >
              No, Keep Me Subscribed
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <Link 
              to="/data-deletion" 
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Looking to delete your account? Request Data Deletion →
            </Link>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
